/**
 * Write — web port of iOS
 * `client-native/.../components/PersonalizedWrite.tsx`.
 *
 * Spell a Spanish word letter-by-letter from its English prompt. Letter tiles
 * (shuffled) are tapped to build the answer; tapping a selected tile removes it;
 * Clear/Back help. On a full word: wrong → red flash → reset after 1000ms (keep
 * trying, NEVER reveal — only a "pstt… it's <word>" hint after 2 misses, as iOS
 * does). Correct → green flash →
 *   - feed: auto onFinish() immediately (iOS PersonalizedWrite:157).
 *   - lesson: 600ms → CorrectDrawer (confirm-to-continue) → onFinish(false)
 *     (iOS PersonalizedWrite:159 + CorrectDrawer 277-287).
 *
 * Responsive (§13): letter tiles are 44×44 and wrap (`flex-wrap`).
 */
import { useEffect, useMemo, useRef, useState } from "react";
import type { WriteActivity } from "@shared_types";
import { shuffleArray } from "@/lib/utils";
import { colors, fontFamily } from "@/ui";
import CorrectDrawer from "../ui/CorrectDrawer";
import type { ActivityComponentContract } from "../types";
import { activityContainerStyle, centeredTitleStyle } from "./layout";

const WRONG_RESET_MS = 1000;
/** Lesson: hold the green flash before sliding up the CorrectDrawer (iOS 600ms). */
const LESSON_CORRECT_DRAWER_DELAY_MS = 600;

type ResponseState = "start" | "incorrect" | "correct";

interface SelectedOption {
  id: number;
  char: string;
}

export interface WriteProps extends ActivityComponentContract {
  data: WriteActivity;
}

export default function Write({
  data,
  variant = "lesson",
  onFinish,
  onTrackAttempt,
}: WriteProps) {
  const isFeed = variant === "feed";
  const answer = (data?.answer ?? "").toLowerCase();
  // PERSONALIZED: individual letters, not syllables, for spelling
  // (iOS PersonalizedWrite.tsx:53-57 — `answerString.split("")`).
  const tiles = useMemo(() => shuffleArray(answer.split("")), [answer]);

  const [selected, setSelected] = useState<SelectedOption[]>([]);
  const [response, setResponse] = useState<ResponseState>("start");
  const [timesIncorrect, setTimesIncorrect] = useState(0);
  const [showCorrectDrawer, setShowCorrectDrawer] = useState(false);
  // Per-activity attempt counter (1,2,3…) → attempt_number / is_first_attempt.
  const attemptRef = useRef(0);

  // Each tile (id = its index in `tiles`) is selectable once.
  const tileSelected = (id: number) => selected.some((s) => s.id === id);

  const handleTile = (char: string, id: number) => {
    // Locked while the wrong-answer reset timer runs, and once correct (the
    // success state holds through LESSON_CORRECT_DRAWER_DELAY_MS before the
    // CorrectDrawer — taps must not clear tiles or undo the green state).
    if (response === "incorrect" || response === "correct") return;
    if (tileSelected(id)) {
      setSelected((prev) => prev.filter((s) => s.id !== id));
      setResponse("start");
      return;
    }
    const next = [...selected, { id, char }];
    setSelected(next);

    if (next.length !== tiles.length) return;

    const got = next.reduce((acc, s) => acc + s.char, "");
    if (got !== answer) {
      onTrackAttempt?.("write", false, ++attemptRef.current);
      setTimesIncorrect((t) => t + 1);
      setResponse("incorrect");
      window.setTimeout(() => {
        setSelected([]);
        setResponse("start");
      }, WRONG_RESET_MS);
      return;
    }

    onTrackAttempt?.("write", true, ++attemptRef.current);
    setResponse("correct");
    // Feed: advance immediately. Lesson: hold then show the CorrectDrawer.
    if (isFeed) onFinish();
    else
      window.setTimeout(
        () => setShowCorrectDrawer(true),
        LESSON_CORRECT_DRAWER_DELAY_MS,
      );
  };

  const handleClear = () => {
    if (response === "correct") return;
    setSelected([]);
    setResponse("start");
  };
  const handleBack = () => {
    if (response === "correct") return;
    if (selected.length > 0) {
      setSelected((prev) => prev.slice(0, -1));
      setResponse("start");
    }
  };

  useEffect(() => {
    setSelected([]);
    setResponse("start");
    setTimesIncorrect(0);
    setShowCorrectDrawer(false);
  }, [answer]);

  if (!data || !data.answer) {
    return (
      <div style={activityContainerStyle(isFeed)}>
        <span style={{ color: colors.muted }}>
          Error: invalid write activity data
        </span>
      </div>
    );
  }

  const answerText = selected.reduce((acc, s) => acc + s.char, "");
  const showHelp = timesIncorrect >= 2;
  const answerBg =
    response === "incorrect"
      ? colors.red
      : response === "correct"
        ? colors.green
        : colors.white;
  const answerColor =
    response === "start" ? colors.blue : colors.white;

  return (
    <div
      style={{
        ...activityContainerStyle(isFeed),
        justifyContent: "space-between",
      }}
    >
      <div
        style={{
          flex: 3,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          gap: 32,
          maxWidth: 600,
          width: "100%",
          alignSelf: "center",
        }}
      >
        <div style={centeredTitleStyle(isFeed)}>
          Spell this word letter by letter
        </div>

        {/* prompt bubble (English word) */}
        <div
          style={{
            backgroundColor: colors.greyChatBubble,
            borderRadius: 16,
            borderBottomLeftRadius: 4,
            padding: "12px 16px",
            alignSelf: "flex-start",
            maxWidth: "80%",
            color: colors.primary,
            fontWeight: 500,
          }}
        >
          {data.question}
        </div>

        {/* answer bubble (what the user has spelled so far) */}
        <div
          style={{
            backgroundColor: answerBg,
            color: answerColor,
            borderRadius: 16,
            borderBottomRightRadius: 4,
            padding: "12px 16px",
            alignSelf: "flex-end",
            minHeight: 44,
            minWidth: 60,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontWeight: 600,
            boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
          }}
        >
          {answerText || " "}
        </div>
      </div>

      <div
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          gap: 24,
          maxWidth: 600,
          width: "100%",
          alignSelf: "center",
        }}
      >
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "center",
            gap: 10,
          }}
        >
          {tiles.map((char, id) => {
            const used = tileSelected(id);
            return (
              <button
                key={id}
                type="button"
                onClick={() => handleTile(char, id)}
                disabled={used}
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: 10,
                  border: "none",
                  background: used ? colors.greyLight : colors.white,
                  color: colors.primary,
                  fontFamily,
                  fontWeight: 700,
                  fontSize: "1rem",
                  cursor: used ? "default" : "pointer",
                  boxShadow: used ? "none" : "0 2px 0 rgba(0,0,0,0.15)",
                  opacity: used ? 0.4 : 1,
                  touchAction: "manipulation",
                }}
              >
                {char}
              </button>
            );
          })}
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "center",
            gap: 12,
            opacity: selected.length > 0 ? 1 : 0,
          }}
        >
          <ActionPill label="Clear" onPress={handleClear} isFeed={isFeed} />
          <ActionPill label="Back" onPress={handleBack} isFeed={isFeed} />
        </div>

        <div
          style={{
            textAlign: "center",
            color: colors.warning,
            fontSize: "0.875rem",
            opacity: showHelp ? 1 : 0,
            minHeight: "1.2em",
          }}
        >
          pstt... it&apos;s {answer}
        </div>
      </div>

      {!isFeed && (
        <CorrectDrawer
          visible={showCorrectDrawer}
          spanishWord={data.answer}
          englishWord={data.question}
          onContinue={() => {
            setShowCorrectDrawer(false);
            onFinish(false);
          }}
        />
      )}
    </div>
  );
}

function ActionPill({
  label,
  onPress,
  isFeed,
}: {
  label: string;
  onPress: () => void;
  isFeed: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onPress}
      style={{
        background: isFeed ? colors.greyProfile : colors.black,
        color: isFeed ? colors.primary : colors.white,
        border: "none",
        borderRadius: 8,
        padding: "8px 12px",
        fontWeight: 600,
        fontSize: "0.875rem",
        cursor: "pointer",
        touchAction: "manipulation",
      }}
    >
      {label}
    </button>
  );
}
